<template>
  <esg-base-chart>
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <apex-chart type="bar"
                ref="chart"
                v-show="!loading"
                :width=this.width
                :height=this.height
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '250',
    },
    legend: {
      type: String,
      default: 'bottom'
    },
    corporation: {
      type: Object,
      required: true,
    }
  },
  watch: {
    corporation: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchData();
      }
    }
  },
  data() {
    return {
      loading: false,
      businessLocations: [],
      series: [],
      chartOptions: {
        chart: {
          fontFamily: 'NanumGothic, serif',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {  //=> bar option
          bar: {
            dataLabels: {
              position: 'top'
            }
          },
        },
        xaxis: {
          categories: [],
          crosshairs: { show: false }
        },
        dataLabels: {
          enabled: true,
          formatter: val => this.formatNumber(val, 2),
          style: {
            fontWeight: 'normal',
            colors: ['#000']
          },
          offsetY: -20
        },
        yaxis: {
          show: true,
          title: {
            text: '탄소 집약도 (tCO2/TJ)',
            style: {
              fontSize: 12,
              fontWeight: 'normal',
            }
          },
          labels: {
            formatter: val => this.compactFormatNumber(val)
          }
        },
        tooltip: {
          y: {
            show: true,
            formatter: val => {
              return this.formatNumber(val, 2) + ' tCO2/TJ'
            }
          },
        },
        grid: {
          show: false
        },
        legend: {
          position: this.legend,
        },
      },
    }
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchData() {
      if (!this.corporation || !this.corporation.code) return;

      try {
        this.loading = true;

        // Fetch business locations
        this.abortController = new AbortController();
        let response = await axios.get('esg/lca/business-location', {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal,
          params: {
            filters: [
              {
                name: 'esg_base_corporation.tenant_id',
                value: this.tenantId(),
                operation: 'EQUAL'
              },
              {
                name: 'esg_base_corporation.code',
                value: this.corporation.code,
                operation: 'EQUAL'
              }
            ]
          }
        });

        if (!response.data.list) return;
        this.businessLocations = response.data.list;

        // Fetch data
        let args = [{
          years: [this.currentYear],
          corporations: [this.corporation.code],
          groupKeys: ['year', 'business_location_code']
        }];

        let data = new FormData()
        data.append('path', 'esg/carbon_intensity/main');
        data.append('json', JSON.stringify(args));

        response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() }
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }
        let list = JSON.parse(response.data.data)[0].data;

        this.setSeries(list);
        this.setLabels();
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setSeries(list) {
      const data = this.businessLocations.map(loc => loc.business_location_code)
          .map(code => list.find(row => row.business_location_code === code).values);
      this.series.push({
        name: '탄소집약도',
        data
      });
    },
    setLabels() {
      this.chartOptions.xaxis.categories = this.businessLocations.map(loc => loc.name);
    }
  }
}
</script>